import { InterviewFeedback } from '../types/interviews';
import { UserInterview } from '../services/interviewService';

export interface LearnifieLinkResponse {
  id: string;
  url: string;
  expiresInSeconds: number;
}

interface LearnifieGoalParams {
  feedback?: InterviewFeedback | null;
  userInterview?: UserInterview | null;
}

const appendLine = (lines: string[], label: string, value?: string | number | null) => {
  if (value === undefined || value === null || value === '') {
    return;
  }
  lines.push(`${label}: ${value}`);
};

export const buildLearnifieGoal = ({ feedback, userInterview }: LearnifieGoalParams): string | null => {
  if (!feedback && !userInterview) {
    return null;
  }

  const detailsFromFeedback = feedback?.interviewDetails;
  const userInterviewDetails = userInterview?.interview || null;

  const lines: string[] = [
    'This is User Feedback for a Mock Interview they gave to practice for the given job details, generate an improvement curriculum for the user based on the feedback'
  ];

  const jobTitle = detailsFromFeedback?.title || userInterviewDetails?.title;
  const company = detailsFromFeedback?.company || userInterviewDetails?.company;
  const role = detailsFromFeedback?.jobRole || userInterviewDetails?.role;
  const interviewLanguage =
    detailsFromFeedback?.language || userInterview?.language || userInterviewDetails?.language;

  const jobDescription =
    detailsFromFeedback?.description || userInterviewDetails?.description || userInterviewDetails?.jobDescription;

  appendLine(lines, 'Interview Title', jobTitle);
  appendLine(lines, 'Company', company);
  appendLine(lines, 'Role', role);
  appendLine(lines, 'Language', interviewLanguage);

  if (typeof feedback?.overallScore === 'number') {
    appendLine(lines, 'Overall Score', feedback.overallScore);
  }

  if (jobDescription) {
    lines.push('Job Description:');
    lines.push(jobDescription);
  }

  if (feedback?.summary) {
    lines.push('Feedback Summary:');
    lines.push(feedback.summary);
  }

  if (feedback?.categories?.length) {
    lines.push('Category Breakdown:');
    feedback.categories.forEach((category) => {
      if (!category) return;
      const parts: string[] = [`- ${category.name}`];
      if (typeof category.score === 'number') {
        parts.push(`score ${category.score}`);
      }
      if (category.feedback) {
        parts.push(`Feedback: ${category.feedback}`);
      }
      lines.push(parts.join('. '));
    });
  }

  if (feedback?.skills?.length) {
    lines.push('Skill Assessments:');
    feedback.skills.forEach((skill) => {
      const feedbackText = skill.feedback ? ` Feedback: ${skill.feedback}` : '';
      lines.push(`- ${skill.name}: score ${skill.score}/5.${feedbackText}`);
    });
  }

  if (feedback?.strengths?.length) {
    lines.push('Strengths Identified:');
    feedback.strengths.forEach((strength) => {
      if (!strength) return;
      const base = `- ${strength.text}`;
      lines.push(strength.reason ? `${base}. Reason: ${strength.reason}` : base);
    });
  }

  if (feedback?.improvements?.length) {
    lines.push('Areas for Improvement:');
    feedback.improvements.forEach((improvement) => {
      if (!improvement) return;
      const base = `- ${improvement.text}`;
      lines.push(improvement.reason ? `${base}. Reason: ${improvement.reason}` : base);
    });
  }

  if (feedback?.impression) {
    lines.push('Overall Impression:');
    appendLine(lines, 'Rating', feedback.impression.rating);
    if (feedback.impression.comment) {
      lines.push(`Comment: ${feedback.impression.comment}`);
    }
  }

  const payload = lines.join('\n').trim();
  return payload.length ? payload : null;
};

export const generateLearnifieLink = async (goal: string): Promise<LearnifieLinkResponse> => {
  const response = await fetch('https://learnifie.com/api/generate-link', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'omit',
    body: JSON.stringify({
      goal,
      mode: 'full'
    })
  });

  if (!response.ok) {
    throw new Error(`Learnifie API responded with status ${response.status}`);
  }

  const data = (await response.json()) as LearnifieLinkResponse;

  if (!data?.url) {
    throw new Error('Learnifie API did not return a URL');
  }

  return data;
};
